/**
 * Cookie Utility Functions
 * 
 * Provides secure cookie management for authentication tokens
 * Supports SameSite, Secure, and custom expiration
 */

/**
 * Set a cookie with secure defaults
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 * @param {number} options.days - Expiration in days (default: 7)
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.sameSite - SameSite attribute (default: 'Strict')
 * @param {boolean} options.secure - Secure flag (default: true in production)
 */
export const setCookie = (name, value, options = {}) => {
    const defaults = {
        days: 7,
        path: '/',
        sameSite: 'Strict',
        secure: window.location.protocol === 'https:' || process.env.NODE_ENV === 'production'
    };

    const config = { ...defaults, ...options };

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Set expiration
    if (config.days) {
        const date = new Date();
        date.setTime(date.getTime() + (config.days * 24 * 60 * 60 * 1000));
        cookieString += `; expires=${date.toUTCString()}`;
    }

    // Set path
    cookieString += `; path=${config.path}`;

    // Set SameSite
    if (config.sameSite) {
        cookieString += `; SameSite=${config.sameSite}`;
    }

    // Set Secure flag
    if (config.secure) {
        cookieString += '; Secure';
    }

    document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.indexOf(nameEQ) === 0) {
            return decodeURIComponent(cookie.substring(nameEQ.length));
        }
    }

    return null;
};

/**
 * Remove a cookie
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const removeCookie = (name, path = '/') => {
    // Set cookie with past expiration date
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

    // Also try with domain for thorough cleanup
    const domain = window.location.hostname;
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;

    // Try with secure flag as well
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; Secure`;
};

/**
 * Get all cookies as an object
 * @returns {Object} Object with cookie names as keys and values
 */
export const getAllCookies = () => {
    const cookies = {};
    const cookieArray = document.cookie.split(';');

    for (let cookie of cookieArray) {
        cookie = cookie.trim();
        const [name, value] = cookie.split('=');
        if (name) {
            cookies[decodeURIComponent(name)] = decodeURIComponent(value || '');
        }
    }

    return cookies;
};

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} True if cookie exists
 */
export const hasCookie = (name) => {
    return getCookie(name) !== null;
};
